import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { Menu, X, Truck, User, LogOut, LayoutDashboard } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav
      data-testid="navbar"
      className="glass-nav sticky top-0 z-50 bg-[#F9F8F6]/80 border-b border-[#E5E0D8]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-9 h-9 bg-[#E68A00] rounded-md flex items-center justify-center transition-colors group-hover:bg-[#CC7A00]">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Creek Bottom <span className="text-[#E68A00]">Rentals</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/equipment"
              data-testid="nav-equipment"
              className="text-sm font-medium text-[#595959] hover:text-[#1A2F24] transition-colors"
            >
              Equipment
            </Link>
            <Link
              to="/#about"
              className="text-sm font-medium text-[#595959] hover:text-[#1A2F24] transition-colors"
            >
              About
            </Link>
            {user && user.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="text-sm font-medium text-[#595959] hover:text-[#1A2F24] transition-colors"
              >
                Admin
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu"
                    className="flex items-center gap-2 text-sm font-medium text-[#1A2F24] hover:text-[#E68A00] transition-colors"
                  >
                    <div className="w-8 h-8 bg-[#1A2F24] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    {user.name}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" data-testid="nav-dashboard-link" className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4" /> My Rentals
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-logout"
                    onClick={logout}
                    className="flex items-center gap-2 text-red-600"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-sm font-medium text-[#1A2F24] hover:text-[#E68A00] transition-colors"
                >
                  Log In
                </Link>
                <Link
                  to="/equipment"
                  data-testid="nav-book-now"
                  className="bg-[#E68A00] hover:bg-[#CC7A00] text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors"
                >
                  Book Now
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#1A2F24]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#E5E0D8] px-4 py-4 space-y-3">
          <Link to="/equipment" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#595959]">
            Equipment
          </Link>
          <Link to="/#about" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#595959]">
            About
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#595959]">
                My Rentals
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#595959]">
                  Admin
                </Link>
              )}
              <button onClick={() => { logout(); setMobileOpen(false); }} className="block text-sm font-medium text-red-600">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#1A2F24]">
                Log In
              </Link>
              <Link
                to="/equipment"
                onClick={() => setMobileOpen(false)}
                className="block bg-[#E68A00] text-white text-center text-sm font-semibold px-5 py-2 rounded-md"
              >
                Book Now
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
